import React, { useEffect, useRef, useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import { PictureAsPdf, RemoveRedEye, EditCalendar } from "@mui/icons-material";
import {
  /* ===== MULTI PO APPOINTMENT SUPPORT =====
     fetchVendorAppointmentDetails is obsolete under the multi-PO structure
     and is replaced by fetchAsnAppointmentDetailsListVendor, called per PO
     (lazy-loaded) against asnAppointmentDetailsListVendor.php.
     NOTE: this function was not present in the ApiService.js provided to me,
     so please add it there following the same pattern as the other
     functions in this import (POST appointmentPoId, return parsed JSON). */
  fetchAsnAppointmentDetailsListVendor,
  fetchVendorAppointmentList,
  fetchAppointmentDateQuantityMasterList,
  rescheduleVendorAppointment,
} from "Service/ApiService";
import { useLoading } from "context/LoadingContext";
import { useAlert } from "context/AlertContext";
import { BASE_URL } from "Service/ApiService";

const EWAY_BILL_BASE_URL = `${BASE_URL}vendor/uploads/asn_EwayBill/`;

const formatDateValue = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AppointmentListController = () => {
  const [appointmentRows, setAppointmentRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoadingState] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [openDetailsPopup, setOpenDetailsPopup] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  /* ===== LAZY LOADING MATERIALS =====
     Per-PO material cache/loading state, replacing the old single
     appointmentDetailRows/detailLoading pair. Mirrors approvedMaterialCache
     from ASNStatusController. */
  const [expandedAppointmentPO, setExpandedAppointmentPO] = useState(null); // appointmentPoId currently expanded, or null
  const [appointmentMaterialCache, setAppointmentMaterialCache] = useState({}); // { [appointmentPoId]: mappedRows[] }
  const [appointmentMaterialLoading, setAppointmentMaterialLoading] = useState({}); // { [appointmentPoId]: true/false }
  const { setLoading } = useLoading();
  const { showAlert } = useAlert();
  const vendorDetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendorDetails?.vendorCode || null;

  // Reschedule feature state
  const [openReschedulePopup, setOpenReschedulePopup] = useState(false);
  const [appointmentDateSlotMap, setAppointmentDateSlotMap] = useState({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [searchPoNumber, setSearchPoNumber] = useState("");
  const poSearchTimerRef = useRef(null);

  const fetchAppointments = async () => {
    if (!vendorCode) {
      setAppointmentRows([]);
      setRowCount(0);
      return;
    }

    try {
      setLoading(true);
      setLoadingState(true);
      const response = await fetchVendorAppointmentList(
        vendorCode,
        paginationModel.page + 1,
        paginationModel.pageSize,
        searchPoNumber.trim()
      );

      if (response?.success === "true") {
        /* ===== MULTI PO APPOINTMENT SUPPORT =====
           Each appointment now returns a poList[] instead of a single
           vendorPoId/poNumber pair. poNumbers/totalPOs are derived helpers
           for display (e.g. the Reschedule dialog chip); poList itself is
           kept on the row for the Details dialog accordions. */
        const rows = (response.asnAppointmentList || []).map((item, index) => {
          const poList = item.poList || [];

          return {
            id: item.asnAppointmentId,
            srNo: paginationModel.page * paginationModel.pageSize + index + 1,
            asnAppointmentId: item.asnAppointmentId,
            vendorCode: item.vendor_code || "-",
            vendorName: item.name || "-",
            poList,
            poNumbers: poList.map((po) => po.poNumber).filter(Boolean).join(", ") || "-",
            totalPOs: poList.length,
            appointmentDate: item.appointmentDate || "-",
            appointmentDateLabel: formatDateValue(item.appointmentDate),
            vendorDispatchQuantity: item.vendor_dispatch_quantity || "0.00",
            ewayBillPdf: item.ewayBillPdf || "",
            isGateEntrySubmit: item.isGateEntrySubmit || "-",
          };
        });

        setAppointmentRows(rows);
        setRowCount(Number(response.pagination?.totalRecords || rows.length || 0));
      } else {
        setAppointmentRows([]);
        setRowCount(0);
      }
    } catch (error) {
      setAppointmentRows([]);
      setRowCount(0);
      showAlert("Unable to fetch appointment list.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setLoading(false);
      setLoadingState(false);
    }
  };

  useEffect(() => {
    poSearchTimerRef.current = setTimeout(() => {
      setSearchPoNumber(poNumber.trim());
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(poSearchTimerRef.current);
  }, [poNumber]);

  useEffect(() => {
    fetchAppointments();
  }, [paginationModel, vendorCode, searchPoNumber]);

  const handlePoClear = () => {
    clearTimeout(poSearchTimerRef.current);
    setPoNumber("");
    setSearchPoNumber("");
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  /* ===== MASTER-DETAIL PO LAYOUT =====
     Opening the dialog no longer eagerly fetches all materials. It shows
     the poList already present on the row in a left-panel PO list, and
     auto-selects the first PO so the right panel isn't empty; materials
     are fetched per PO only when selected (see handleSelectAppointmentPo
     below). */

  const handleViewDetails = (row) => {
    const poList = row?.poList || [];
    const firstPo = poList[0];

    setSelectedAppointment(row);
    setAppointmentMaterialCache({});
    setAppointmentMaterialLoading({});
    setExpandedAppointmentPO(firstPo ? firstPo.appointmentPoId : null);
    setOpenDetailsPopup(true);

    if (firstPo) {
      ensureAppointmentPoMaterialsLoaded(firstPo.appointmentPoId);
    }
  };

  const handleCloseDetailsPopup = () => {
    setOpenDetailsPopup(false);
    setSelectedAppointment(null);
    setExpandedAppointmentPO(null);
    setAppointmentMaterialCache({});
    setAppointmentMaterialLoading({});
  };

  /* ===== LAZY LOADING MATERIALS =====
     Fetches materials for a PO the first time it's needed and caches the
     result thereafter. Logic/caching unchanged from the previous
     implementation - only extracted so it can be reused by both the
     auto-select-on-open above and the PO-click handler below. */
  const ensureAppointmentPoMaterialsLoaded = async (appointmentPoId) => {
    if (appointmentMaterialCache[appointmentPoId] || appointmentMaterialLoading[appointmentPoId]) {
      return;
    }

    try {
      setAppointmentMaterialLoading((prev) => ({ ...prev, [appointmentPoId]: true }));
      const response = await fetchAsnAppointmentDetailsListVendor(appointmentPoId);

      if (response?.success === "true") {
        const rows = (response.asnAppointmentDetailsList || []).map((item) => ({
          id: item.asnAppointmentDtlsId,
          materialCode: item.materialCode || "-",
          boxNumber: item.boxNumber || "-",
          brand: item.brand || "-",
          product: item.product || "-",
          materialDesc: item.materialDesc || "-",
          barcode: item.barcode || "-",
          quantity: item.quantity || "0.00",
        }));

        setAppointmentMaterialCache((prev) => ({ ...prev, [appointmentPoId]: rows }));
      } else {
        setAppointmentMaterialCache((prev) => ({ ...prev, [appointmentPoId]: [] }));
        showAlert(response?.msg || "No appointment details found.", {
          icon: "info",
          title: "No Data",
        });
      }
    } catch (error) {
      setAppointmentMaterialCache((prev) => ({ ...prev, [appointmentPoId]: [] }));
      showAlert("Unable to fetch appointment details.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setAppointmentMaterialLoading((prev) => ({ ...prev, [appointmentPoId]: false }));
    }
  };

  /* ===== MASTER-DETAIL PO LAYOUT =====
     Left panel is a single-select list, like ASNStatus's PO list: clicking
     a PO makes it the active PO shown in the right-hand materials panel.
     Nothing collapses - exactly one PO stays active at a time. */
  const handleSelectAppointmentPo = async (appointmentPoId) => {
    setExpandedAppointmentPO(appointmentPoId);
    await ensureAppointmentPoMaterialsLoaded(appointmentPoId);
  };

  const handleOpenEwayBill = (fileName) => {
    if (!fileName || !String(fileName).trim()) {
      return;
    }

    const fileUrl = `${EWAY_BILL_BASE_URL}${encodeURIComponent(fileName)}`;
    const link = document.createElement("a");

    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ===================== Reschedule: calendar helper logic (reused from ASNStatusController) =====================

  const getMonthLabel = (dateValue) =>
    dateValue.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

  const normalizeDateKey = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
  };

  const todayDateKey = normalizeDateKey(new Date());

  const isPastDate = (dateKey) => {
    if (!dateKey) {
      return false;
    }

    return dateKey < todayDateKey;
  };

  const isCurrentOrFutureMonth = (monthDate) => {
    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const selectedMonthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);

    return selectedMonthStart >= currentMonthStart;
  };

  const getCalendarDays = (monthDate) => {
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const days = [];

    for (let i = 0; i < startDay; i += 1) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const dateValue = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      days.push({
        day,
        dateKey: normalizeDateKey(dateValue),
      });
    }

    return days;
  };

  const handleCalendarMonthChange = (direction) => {
    setCurrentCalendarMonth((prev) => {
      const nextMonth = new Date(prev.getFullYear(), prev.getMonth() + direction, 1);
      return isCurrentOrFutureMonth(nextMonth) ? nextMonth : prev;
    });
  };

  const getSlotStatus = (dateKey) => {
    if (isPastDate(dateKey)) {
      return "Closed";
    }

    const slot = appointmentDateSlotMap[dateKey];

    if (!slot) {
      return "No Slot Available";
    }

    return Number(slot.quantityPercentExtra || 0) > 0 ? "Available" : "Fully Booked";
  };

  const isSlotDateAvailable = (dateKey) => getSlotStatus(dateKey) === "Available";

  const expandAppointmentDateSlots = (slotList = []) => {
    const expandedSlots = {};

    slotList.forEach((slot) => {
      if (slot?.datesMaster) {
        const normalizedDate = normalizeDateKey(slot.datesMaster);

        if (!normalizedDate) {
          return;
        }

        expandedSlots[normalizedDate] = {
          ...slot,
          quantity: Number(slot.quantity || 0),
          quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
          dateKey: normalizedDate,
        };
        return;
      }

      if (slot?.fromDate && slot?.toDate) {
        const startDate = new Date(slot.fromDate);
        const endDate = new Date(slot.toDate);

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          return;
        }

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateKey = normalizeDateKey(currentDate);
          expandedSlots[dateKey] = {
            ...slot,
            quantity: Number(slot.quantity || 0),
            quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
            dateKey,
          };
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    return expandedSlots;
  };

  const getAppointmentDateSlots = async () => {
    try {
      const response = await fetchAppointmentDateQuantityMasterList();

      if (response?.success === "true") {
        const expandedSlots = expandAppointmentDateSlots(
          response.appointmentDateQntyMasterList || []
        );

        setAppointmentDateSlotMap(expandedSlots);
      } else {
        setAppointmentDateSlotMap({});
      }
    } catch (error) {
      setAppointmentDateSlotMap({});
    }
  };

  // ===================== Reschedule: dialog open/close & submit =====================

  const handleOpenReschedule = async (row) => {
    setSelectedAppointment(row);
    setSelectedCalendarDate("");
    setOpenReschedulePopup(true);

    const existingAppointmentDate = new Date(row?.appointmentDate);
    const initialMonth = Number.isNaN(existingAppointmentDate.getTime())
      ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      : new Date(existingAppointmentDate.getFullYear(), existingAppointmentDate.getMonth(), 1);

    setCurrentCalendarMonth(
      isCurrentOrFutureMonth(initialMonth)
        ? initialMonth
        : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );

    try {
      setRescheduleLoading(true);
      await getAppointmentDateSlots();
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCloseReschedulePopup = () => {
    setOpenReschedulePopup(false);
    setSelectedAppointment(null);
    setSelectedCalendarDate("");
    setRescheduleLoading(false);
    setRescheduleSubmitting(false);
  };

  const handleRescheduleDateSelect = (dateKey) => {
    if (!isSlotDateAvailable(dateKey)) {
      return;
    }

    if (dateKey === normalizeDateKey(selectedAppointment?.appointmentDate)) {
      return;
    }

    setSelectedCalendarDate(dateKey);
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment?.asnAppointmentId || !selectedCalendarDate) {
      return;
    }

    try {
      setRescheduleSubmitting(true);
      const response = await rescheduleVendorAppointment({
        asnAppointmentId: selectedAppointment.asnAppointmentId,
        rescheduleAppointmentDate: selectedCalendarDate,
      });

      if (response?.success === "true") {
        showAlert(response?.msg || "Appointment rescheduled successfully.", {
          icon: "success",
          title: "Success",
        });
        handleCloseReschedulePopup();
        await fetchAppointments();
      } else {
        showAlert(response?.msg || "Unable to reschedule appointment.", {
          icon: "error",
          title: "Error",
        });
      }
    } catch (error) {
      showAlert("Unable to reschedule appointment.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setRescheduleSubmitting(false);
    }
  };

  /* ===== REMOVED PO NUMBER COLUMN ===== */
  const columns = [
    { field: "srNo", headerName: "Sr No", flex: 0.3 },
    // { field: "vendorCode", headerName: "Vendor Code", flex: 1, minWidth: 130 },
    // { field: "vendorName", headerName: "Vendor Name", flex: 1.4, minWidth: 180 },
    // { field: "poNumbers", headerName: "PO Numbers", flex: 1.5, minWidth: 150 },
    {
      field: "poNumbers",
      headerName: "PO Numbers",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: "1.4",
            width: "100%",
            padding: "8px 0",
          }}
        >
          {params.value || "-"}
        </div>
      ),
    },
    { field: "appointmentDateLabel", headerName: "Appointment Date", flex: 0.6, minWidth: 120 },
    {
      field: "vendorDispatchQuantity",
      headerName: "Dispatch Qty",
      flex: 0.5,
      minWidth: 130,
    },
    { field: "isGateEntrySubmit", headerName: "Gate Entry", flex: 0.4, minWidth: 100 },
    {
      field: "ewayBillPdf",
      headerName: "E-way Bill PDF",
      minWidth: 130,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasEwayBill = Boolean(params.row.ewayBillPdf && params.row.ewayBillPdf.trim());

        return (
          <Button
            disabled={!hasEwayBill}
            onClick={(event) => {
              event.stopPropagation();
              if (hasEwayBill) {
                handleOpenEwayBill(params.row.ewayBillPdf);
              }
            }}
            startIcon={<PictureAsPdf sx={{ fontSize: "2rem !important" }} />}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="View Details">
            <IconButton color="error" size="small" onClick={() => handleViewDetails(params.row)}>
              <RemoveRedEye fontSize="small" />
            </IconButton>
          </Tooltip>
          {String(params.row.isGateEntrySubmit).toLowerCase() === "no" && (
            <Tooltip title="Reschedule Appointment">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleOpenReschedule(params.row)}
              >
                <EditCalendar fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const detailColumns = [
    { accessor: "materialCode", Header: "Material Code", field: "materialCode", flex: 1.2, minWidth: 160 },
    { accessor: "boxNumber", Header: "Box Number", field: "boxNumber", flex: 0.8, minWidth: 110 },
    { accessor: "brand", Header: "Brand", field: "brand", flex: 1, minWidth: 120 },
    { accessor: "product", Header: "Product", field: "product", flex: 1, minWidth: 120 },
    { accessor: "materialDesc", Header: "Material Desc", field: "materialDesc", flex: 1.6, minWidth: 220 },
    { accessor: "barcode", Header: "Barcode", field: "barcode", flex: 1.2, minWidth: 170 },
    { accessor: "quantity", Header: "Quantity", field: "quantity", flex: 0.8, minWidth: 100 },
  ];

  const getAvailableQuantity = (dateKey) => {
    return Number(appointmentDateSlotMap[dateKey]?.quantityPercentExtra || 0);
  };

  return {
    loading,
    appointmentRows,
    rowCount,
    paginationModel,
    setPaginationModel,
    columns,

    poNumber,
    setPoNumber,
    handlePoClear,
    
    openDetailsPopup,
    handleCloseDetailsPopup,
    selectedAppointment,
    detailColumns,
    /* ===== LAZY LOADING MATERIALS ===== */
    expandedAppointmentPO,
    appointmentMaterialCache,
    appointmentMaterialLoading,
    handleSelectAppointmentPo,

    // Reschedule feature
    openReschedulePopup,
    handleCloseReschedulePopup,
    currentCalendarMonth,
    handleCalendarMonthChange,
    getCalendarDays,
    getMonthLabel,
    isPastDate,
    isCurrentOrFutureMonth,
    getSlotStatus,
    getAvailableQuantity,
    isSlotDateAvailable,
    normalizeDateKey,
    selectedCalendarDate,
    handleRescheduleDateSelect,
    handleRescheduleSubmit,
    rescheduleLoading,
    rescheduleSubmitting,
    formatDateValue,
  };
};

export default AppointmentListController;